import { Component,Input, SimpleChanges } from '@angular/core';
import { chart } from '@lib/angular/chart';
import { DateTime } from '@lib/date-time';
import { op, taste } from '@lib/rxjs';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, Observable } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { fragQuery } from '../../../../../../shared-models/frag-query';
import { Device, User } from '@models';
import { DataPoint, generateChartLines, generateTimelineChartLines } from '@app/new-ui/chart-transforms';
import { Router } from '@angular/router';
import * as Highcharts from 'highcharts/highstock';
import { combineInput } from '@app/new-ui/analysis/analysis.component';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { DateFormatConfig } from '@app/utilities/date-format-config';
import { asFiniteNumber } from '@lib/number';
@Component({
    selector: 'snapshot-card-home',
    templateUrl: './snapshot-card-home.component.html',
    styleUrls: ['./snapshot-card-home.component.scss'],
})
export class SnapshotCardHomeComponent {

    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly vehicleQuery: VehicleQueryService,
        private readonly user: User,
        private readonly router: Router,
        private readonly dateFormat: DateFormatConfig
    ) { }

    @Input()
    get timer() {
        return this.timer$.value;
    }
    set timer(v) {
        this.timer$.next(v);
    }
    @Input() public device: Device;
    @Input() public series: fragQuery.timeline.sieveSizesAtPercents.Response = null;

    get deviceId(): number | null {
        return this.series?.deviceId ? asFiniteNumber(this.series.deviceId) : null;
    }

    public intervalsArray: fragQuery.timeline.sieveSizesAtPercents.Interval[];
    public currentImgIndex = 0;
    public readonly timer$ = new BehaviorSubject('');
    public currentInterval = new BehaviorSubject(null);
    public readonly percents$ = new BehaviorSubject(this.fragQuery.newPercents$.value);
    public readonly dateTimeFormat = this.dateFormat.getFormat()
    public xHighlight: DateTime.Range = null;
    public unitSystem: string = this.user.particleUnit;
    public Highcharts: typeof Highcharts = Highcharts;
    public chartOptions:Highcharts.Options;

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['series'] && changes['series'].currentValue) {
            if (this.series && this.series !== null) {
                this.intervalsArray = this.series.intervals
                this.currentInterval.next(this.series.intervals[0])
            }
        }
    }

    public onPlotMouseMove(evt: chart.line.PlotEvent) {
        if (evt.series) {
            const input = taste(this.fragQuery.timelineSieveSizesAtPercents$);
            if (input) {
                const dataPoint = <DataPoint>(
                    evt.series.data[Math.floor(evt.dataIndex + 0.5)]
                );
                if (dataPoint) {
                    const interval = dataPoint[2];
                    if (interval) {
                        this.xHighlight = new DateTime.Range(interval.begin, interval.end);
                    }
                }
            }
        }
    }

    setDevice(){
        let url = '/analytics#';
        url += JSON.stringify({ device: this.series.deviceId, site: this.series.siteId });
        this.router.navigateByUrl(url);
    }

    handleNextClick() {
        const next = this.currentImgIndex + 1;
        this.currentImgIndex = next === this.intervalsArray.length ? 0 : next;
        this.currentInterval.next(this.intervalsArray[this.currentImgIndex]) 
    }

    handlePreviousClick() {
        const previous = this.currentImgIndex - 1;
        this.currentImgIndex = previous < 0 ? this.intervalsArray.length - 1 : previous;
        this.currentInterval.next(this.intervalsArray[this.currentImgIndex]) 
    }

    public readonly series$ = this.percents$.pipe(
        op.map((percents) => {
            if(this.series){
                return generateChartLines(percents, this.series);
            }else{
                return null;
            }
        }),
        op.shareReplay(1)
    )

    public readonly chartLines$:Observable<Highcharts.Options>= combineLatest([
        this.percents$,
        this.fragQuery.timelineSieveSizesAtPercents$,
        this.fragQuery.addOnTimelineSieveSizesAtPercents$,
        this.fragQuery.particleSizeUnit$,
        this.fragQuery.muckpiles$,
        this.vehicleQuery.vehicles$
    ]).pipe(
        op.tap(([percents,input,addOn])=>combineInput(input,addOn,this.fragQuery.addDataAt.value)),
        op.tap(input=>{
            if (input[1] && input[1].intervals) {
                this.fragQuery.totalIntervalLength.next((input[1].end.valueOf() - input[1].begin.valueOf()))
                this.fragQuery.intervalsLength.next(input[1].intervals.length)
            }
        }),
        op.map(([percents, input,input2,unit,muckpiles, vehicles]) => {
            const timelineChartConfig = {
                chartType: 'line' as const,
                percents,
                input:this.series,
                particleSizeUnit: unit,
                zoomingEnable: false,
                navigatorEnable: false,
                tooltipEnable: true,
                scrollbarEnable: true,
                addData: this.addData.bind(this),
                plotClick: this.onPlotClick.bind(this),
                muckpiles,
                vehicles
            }
            return generateTimelineChartLines(timelineChartConfig);
        })
    )

    addData(type:string){
        this.fragQuery.addDataAt.next(type);
    }

    async onPlotClick (time:number){
        let dataPoint;
        let intervalIndex = -1
        for(let index= 0; index < this.series.intervals.length; index++) {
            const interval=this.series.intervals[index];
            if(interval.begin.valueOf()<=time){
                dataPoint=interval;
                intervalIndex = index
            }else{
                break;
            }
        }
        if(dataPoint){
            this.currentInterval.next(dataPoint)
            this.currentImgIndex = intervalIndex
        };
    }
    
    public readonly intervals$ = this.percents$.pipe(
        op.map(percents =>{
            if(this.series && this.series.intervals){
                return this.series.intervals
            }else{
                return null;
            }
        })
    )


}